import {
  Icon,
  IconArrowNarrowDown,
  IconArrowNarrowLeft,
  IconArrowNarrowRight,
  IconArrowNarrowUp,
  IconArrowsMove,
  IconBolt,
  IconBook,
  IconClock,
  IconCloudFog,
  IconDeviceTv,
  IconDrone,
  IconFlame,
  IconFocus2,
  IconHelicopter,
  IconHomeDown,
  IconHomeUp,
  IconHourglassHigh,
  IconLeaf,
  IconMoon,
  IconMoonStars,
  IconNews,
  IconPencil,
  IconPhotoBolt,
  IconPropeller,
  IconRipple,
  IconSnowflake,
  IconSofa,
  IconSparkle,
  IconSparkles2,
  IconSun,
  IconSunrise,
  IconSunset,
  IconSunset2,
  IconVideo,
  IconView360Arrow,
  IconViewfinder,
} from "@tabler/icons-react";
import type { CameraMovement, AnimationStyle } from "@repo/api/animated-image";
import {
  parseAnimatedImagePromptType,
  type ParsedPromptType,
} from "@repo/api/utils/prompt-type";

export type SelectOption<T> = {
  value: T;
  label: string;
  icon: React.ElementType;
  isPopular?: boolean;
  videoPreview?: boolean;
  explanation?: string;
};

export type AnimationStep =
  | {
      type: "SELECT_CAMERA_MOVEMENT";
      options: SelectOption<CameraMovement>[];
      nextStepExplanation?: string;
    }
  | {
      type: "SELECT_STYLE";
      options: SelectOption<AnimationStyle>[];
      nextStepExplanation?: string;
    }
  | {
      type: "SELECT_END_IMAGE";
    };

export type AnimationCategory = {
  id: string;
  label: string;
  icon: Icon;
  steps: AnimationStep[];
};

export const cameraMovementCatalog: Record<
  CameraMovement,
  SelectOption<CameraMovement>
> = {
  PUSH_IN: {
    value: "PUSH_IN",
    label: "Push In",
    icon: IconViewfinder,
    isPopular: true,
    videoPreview: true,
  },
  ROTATE: {
    value: "ROTATE",
    label: "Rotate",
    icon: IconView360Arrow,
    isPopular: true,
    videoPreview: true,
  },
  PULL_OUT: {
    value: "PULL_OUT",
    label: "Pull Out",
    icon: IconFocus2,
    videoPreview: true,
  },
  TRUCK_LEFT: {
    value: "TRUCK_LEFT",
    label: "Truck Left",
    icon: IconArrowNarrowLeft,
    videoPreview: true,
  },
  TRUCK_RIGHT: {
    value: "TRUCK_RIGHT",
    label: "Truck Right",
    icon: IconArrowNarrowRight,
    videoPreview: true,
  },
  DRONE_UP: {
    value: "DRONE_UP",
    label: "Drone Up",
    icon: IconDrone,
    isPopular: true,
    videoPreview: true,
  },
  STATIC_SHOT: {
    value: "STATIC_SHOT",
    label: "Static Shot",
    icon: IconSnowflake,
    videoPreview: true,
  },
  PEDESTAL_UP: {
    value: "PEDESTAL_UP",
    label: "Pedestal Up",
    icon: IconArrowNarrowUp,
    videoPreview: true,
  },
  PEDESTAL_DOWN: {
    value: "PEDESTAL_DOWN",
    label: "Pedestal Down",
    icon: IconArrowNarrowDown,
    videoPreview: true,
  },
};

export const styleCatalog: Record<
  AnimationStyle,
  SelectOption<AnimationStyle>
> = {
  SUNSET: {
    value: "SUNSET",
    label: "Sunset",
    icon: IconSunset2,
    explanation: "Makes the video look like it was filmed at sunset.",
  },
  TWILIGHT: {
    value: "TWILIGHT",
    label: "Twilight",
    icon: IconMoonStars,
    explanation: "Makes the video look like it was filmed at twilight.",
  },
  NIGHT: {
    value: "NIGHT",
    label: "Night",
    icon: IconMoon,
    explanation: "Makes the video look like it was filmed at night.",
  },
  SUNRISE: {
    value: "SUNRISE",
    label: "Sunrise",
    icon: IconSunrise,
    explanation: "Makes the video look like it was filmed at sunrise.",
  },
  MIDDAY: {
    value: "MIDDAY",
    label: "Midday",
    icon: IconSun,
    explanation: "Makes the video look like it was filmed at midday.",
  },
  AFTERNOON: {
    value: "AFTERNOON",
    label: "Afternoon",
    icon: IconClock,
    explanation: "Makes the video look like it was filmed at afternoon.",
  },
  CEILING_FANS: {
    value: "CEILING_FANS",
    label: "Spinning Fans",
    icon: IconPropeller,
    explanation: "Makes the fans in the scene spin.",
  },
  WATER: {
    value: "WATER",
    label: "Rippling Water",
    icon: IconRipple,
    explanation: "Makes the water in the scene ripple and flow naturally.",
  },
  FOG: {
    value: "FOG",
    label: "Fog",
    icon: IconCloudFog,
    explanation: "The camera pushes through the fog and reveals the scene.",
    videoPreview: true,
  },
  HELICOPTER: {
    value: "HELICOPTER",
    label: "Helicopter",
    icon: IconHelicopter,
    explanation: "A helicopter pulls away the white sheet wrapping the house.",
    videoPreview: true,
  },
  HOUSE_DROP: {
    value: "HOUSE_DROP",
    label: "House Drop",
    icon: IconHomeDown,
    explanation: "The house drops down into the scene.",
    videoPreview: true,
  },
  HOUSE_JUMP: {
    value: "HOUSE_JUMP",
    label: "House Jump",
    icon: IconHomeUp,
    explanation: "The house jumps up and lands back on the ground.",
    videoPreview: true,
  },
  PENCIL_DRAWING: {
    value: "PENCIL_DRAWING",
    label: "Pencil Drawing",
    icon: IconPencil,
    explanation: "A pencil drawing of the scene turns into the real scene.",
    videoPreview: true,
  },
  FURNITURE_REVEAL: {
    value: "FURNITURE_REVEAL",
    label: "Furniture Reveal",
    icon: IconSofa,
    explanation: "The furniture is revealed gradually as the camera rolls.",
    videoPreview: true,
  },
  FURNITURE_REVEAL_SE: {
    value: "FURNITURE_REVEAL_SE",
    label: "Furniture Reveal",
    icon: IconSofa,
    explanation:
      "The furniture is revealed gradually as the camera moves between the images.",
    videoPreview: true,
  },
  MAGAZINE: {
    value: "MAGAZINE",
    label: "Magazine",
    icon: IconBook,
    explanation: "The magazine turns into the real scene.",
    videoPreview: true,
  },
  NEWSPAPER: {
    value: "NEWSPAPER",
    label: "Newspaper",
    icon: IconNews,
    explanation: "The newspaper turns into the real scene.",
    videoPreview: true,
  },
  FIREPLACE: {
    value: "FIREPLACE",
    label: "Flickering Fireplace",
    icon: IconFlame,
    explanation: "Lights the fireplaces and makes the flames flicker.",
  },
  DECLUTTER: {
    value: "DECLUTTER",
    label: "Declutter",
    icon: IconSparkles2,
    explanation:
      "Removes clutter like personal items and trash from the scene.",
  },
  TV: {
    value: "TV",
    label: "Cinematic TV",
    icon: IconDeviceTv,
    explanation: "Displays cinematic forest creek footage on TV screens.",
  },
  TIMELAPSE_DAY_TO_NIGHT: {
    value: "TIMELAPSE_DAY_TO_NIGHT",
    label: "Day to Night",
    icon: IconMoon,
    videoPreview: true,
    explanation: "A timelapse from day to night with the camera staying still.",
  },
  TIMELAPSE_NIGHT_TO_DAY: {
    value: "TIMELAPSE_NIGHT_TO_DAY",
    label: "Night to Day",
    icon: IconSun,
    videoPreview: true,
    explanation: "A timelapse from night to day with the camera staying still.",
  },
  TIMELAPSE_SUNRISE: {
    value: "TIMELAPSE_SUNRISE",
    label: "Night to Sunrise",
    icon: IconSunrise,
    videoPreview: true,
    explanation:
      "A timelapse from night to sunrise with the camera staying still.",
  },
  TIMELAPSE_SUNSET: {
    value: "TIMELAPSE_SUNSET",
    label: "Afternoon to Sunset",
    icon: IconSunset,
    videoPreview: true,
    explanation:
      "A timelapse from afternoon to sunset with the camera staying still.",
  },
  SPEED_RAMP: {
    value: "SPEED_RAMP",
    label: "Speed Ramp",
    icon: IconBolt,
    videoPreview: true,
    explanation:
      "Speed ramp between the images, with acceleration in the middle.",
  },
  STEADY_GLIDE: {
    value: "STEADY_GLIDE",
    label: "Steady Glide",
    icon: IconArrowsMove,
    videoPreview: true,
    explanation: "The camera moves between the images with a constant speed.",
  },
  AERIAL_TRANSITION: {
    value: "AERIAL_TRANSITION",
    label: "Aerial Transition",
    icon: IconDrone,
    videoPreview: true,
    explanation: "Transition between the images with a drone-like movement.",
  },
};

export const cameraMovementStep: AnimationStep = {
  type: "SELECT_CAMERA_MOVEMENT",
  options: [
    cameraMovementCatalog.PUSH_IN,
    cameraMovementCatalog.ROTATE,
    cameraMovementCatalog.DRONE_UP,
    cameraMovementCatalog.TRUCK_LEFT,
    cameraMovementCatalog.TRUCK_RIGHT,
    cameraMovementCatalog.PULL_OUT,
    cameraMovementCatalog.PEDESTAL_UP,
    cameraMovementCatalog.PEDESTAL_DOWN,
    cameraMovementCatalog.STATIC_SHOT,
  ],
};

const cameraMovementStepExplanation = "Next step: Camera Movement";

export const quickAnimationCategories: AnimationCategory[] = [
  {
    id: "CAMERA_MOVEMENT",
    label: "Camera Movement",
    icon: IconVideo,
    steps: [cameraMovementStep],
  },
  {
    id: "AMBIENT",
    label: "Ambient",
    icon: IconLeaf,
    steps: [
      {
        type: "SELECT_STYLE",
        options: [
          styleCatalog.TV,
          styleCatalog.FIREPLACE,
          styleCatalog.DECLUTTER,
          styleCatalog.CEILING_FANS,
          styleCatalog.WATER,
        ],
        nextStepExplanation: cameraMovementStepExplanation,
      },
      cameraMovementStep,
    ],
  },
  {
    id: "TIMELAPSE",
    label: "Timelapse",
    icon: IconHourglassHigh,
    steps: [
      {
        type: "SELECT_STYLE",
        options: [
          styleCatalog.TIMELAPSE_SUNRISE,
          styleCatalog.TIMELAPSE_SUNSET,
          styleCatalog.TIMELAPSE_DAY_TO_NIGHT,
          styleCatalog.TIMELAPSE_NIGHT_TO_DAY,
        ],
      },
    ],
  },
  {
    id: "TIME_OF_DAY",
    label: "Time of Day",
    icon: IconClock,
    steps: [
      {
        type: "SELECT_STYLE",
        options: [
          styleCatalog.SUNSET,
          styleCatalog.TWILIGHT,
          styleCatalog.NIGHT,
          styleCatalog.SUNRISE,
          styleCatalog.MIDDAY,
          styleCatalog.AFTERNOON,
        ],
        nextStepExplanation: cameraMovementStepExplanation,
      },
      cameraMovementStep,
    ],
  },
  {
    id: "SPECIAL_EFFECTS",
    label: "Special Effects",
    icon: IconSparkle,
    steps: [
      {
        type: "SELECT_STYLE",
        options: [
          styleCatalog.FOG,
          styleCatalog.HELICOPTER,
          styleCatalog.PENCIL_DRAWING,
          styleCatalog.HOUSE_DROP,
          styleCatalog.HOUSE_JUMP,
          styleCatalog.FURNITURE_REVEAL,
          styleCatalog.MAGAZINE,
          styleCatalog.NEWSPAPER,
        ],
      },
    ],
  },
  {
    id: "START_END_IMAGE",
    label: "Start & End Image",
    icon: IconPhotoBolt,
    steps: [
      {
        type: "SELECT_STYLE",
        options: [
          styleCatalog.SPEED_RAMP,
          styleCatalog.STEADY_GLIDE,
          styleCatalog.AERIAL_TRANSITION,
          styleCatalog.FURNITURE_REVEAL_SE,
        ],
        nextStepExplanation: "Next step: Add end image",
      },
      { type: "SELECT_END_IMAGE" },
    ],
  },
];

const styleTimelineDurationOverrides: Partial<Record<AnimationStyle, number>> =
  {
    TIMELAPSE_DAY_TO_NIGHT: 5,
    TIMELAPSE_NIGHT_TO_DAY: 5,
    TIMELAPSE_SUNRISE: 5,
    TIMELAPSE_SUNSET: 5,
    SPEED_RAMP: 3,
    STEADY_GLIDE: 5,
    AERIAL_TRANSITION: 5,
    FOG: 5,
    HELICOPTER: 5,
    HOUSE_DROP: 5,
    HOUSE_JUMP: 4,
    PENCIL_DRAWING: 3,
    FURNITURE_REVEAL: 5,
    FURNITURE_REVEAL_SE: 5,
    MAGAZINE: 4,
    NEWSPAPER: 4,
  };

export function getAnimatedImageDurationForComposition(
  parsed: ParsedPromptType,
): number | undefined {
  if (parsed.style && styleTimelineDurationOverrides[parsed.style]) {
    return styleTimelineDurationOverrides[parsed.style];
  }

  return undefined;
}

export const getTriggerLabel = (promptType: string): string => {
  const parsed = parseAnimatedImagePromptType(promptType);

  const parts: string[] = [];

  if (parsed.style) {
    parts.push(styleCatalog[parsed.style].label);
  }

  if (parsed.cameraMovement) {
    parts.push(cameraMovementCatalog[parsed.cameraMovement].label);
  }

  return parts.join(" - ");
};

export function getDialogTitle(currentStep: AnimationStep): string {
  switch (currentStep.type) {
    case "SELECT_STYLE":
      return "Select a style";
    case "SELECT_CAMERA_MOVEMENT":
      return "Select a camera movement";
    case "SELECT_END_IMAGE":
      return "Select an end image";
  }
}

export function getDialogDescription(
  style: AnimationStyle | null,
  cameraMovement: CameraMovement | null,
): string | undefined {
  const parts: string[] = [];

  if (style) {
    parts.push(styleCatalog[style].label);
  }

  if (cameraMovement) {
    parts.push(cameraMovementCatalog[cameraMovement].label);
  }

  if (parts.length === 0) {
    return undefined;
  }

  return parts.join(" - ");
}
